import { createContext, use } from 'react';

import type { AuthState, Capability, Role, SessionEndReason, SessionUser } from '../types';

export type { AuthStatus, SessionEndReason, SignInFailure } from '../types';

export interface AuthContextValue extends AuthState<SessionUser> {
  /**
   * Whether the server granted a capability. For choosing what to *show* only.
   *
   * NOT a security control (`docs/api.md` §4.2, `docs/requirements.md` NFR-01):
   * every request is re-authorised by the API. Hiding a link grants nothing.
   */
  readonly can: (capability: Capability) => boolean;
  /** Any admin capability — shows the admin navigation and routes. UI only. */
  readonly canUseAdminArea: boolean;
  /**
   * "Continue with Microsoft". Sends the browser to Microsoft's hosted sign-in; the
   * page normally unloads, and the session is completed when Microsoft redirects
   * back. Resolves only if that navigation failed, with `signInFailure` set.
   */
  signInWithMicrosoft: (returnTo: string | null) => Promise<void>;
  /** `POST /auth/logout`. Always ends the local session. */
  logout: () => Promise<void>;
  /** `GET /auth/me`: re-reads the user so role and capability changes appear. */
  reloadUser: () => Promise<void>;
  /** Re-attempts session restore after the API was unreachable. */
  retry: () => void;
  /**
   * DEV-1/DEV-2: `POST /auth/dev-login { role }`, then `GET /auth/me` — a real
   * session for that role's development account (`dev-auth/dev-auth.ts`). Rejects
   * with the API error. Always `null` outside a development build with
   * `VITE_DEV_AUTH_BYPASS=true`.
   */
  readonly signInAsDevUser: ((role: Role) => Promise<void>) | null;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Reads the auth context.
 *
 * Throws when used outside the provider rather than returning `null`: a silently
 * unauthenticated tree is a security-adjacent bug, not a convenience.
 */
export function useAuth(): AuthContextValue {
  const value = use(AuthContext);

  if (value === null) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }

  return value;
}

export function isSignedOut(reason: SessionEndReason | null): boolean {
  return reason === 'signed-out' || reason === 'signed-out-unconfirmed';
}
